import { ChevronRight, MoreVertical, Power, Save, ShieldOff, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useAccessRole, useUpdateRolePermissions } from './api'
import { StatusBadge } from './components/AccessBits'
import { toastError } from './ui'
import { AssignUsersDialog } from './components/AssignUsersDialog'
import { ChangeSummaryBar, SaveChangesModal } from './components/ChangeSummaryBar'
import { PermissionDetailPanel } from './components/PermissionDetailPanel'
import { PermissionMatrix } from './components/PermissionMatrix'
import { RoleFormModal } from './components/RoleFormModal'
import { RoleHistory } from './components/RoleHistory'
import { RoleGeneralCard, RoleVersionCard } from './components/RoleInfoCards'
import { RoleUsersTable } from './components/RoleUsersTable'
import { useRoleCommands } from './components/useRoleCommands'
import { diffPermissions, ROLE_WRITE_PERM, toggleMany } from './permissions'
import type { Role } from './types'

export function RoleDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const role = useAccessRole(id)

  if (role.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-96" />
      </div>
    )
  }
  if (role.isError) {
    return isApiError(role.error) && role.error.code === 'NOT_FOUND' ? (
      <EmptyState
        icon={ShieldOff}
        title={t('access.detail.notFound')}
        action={
          <Button variant="outline" asChild>
            <Link to="/access/roles">{t('access.detail.backToRoles')}</Link>
          </Button>
        }
      />
    ) : (
      <ErrorState error={role.error} onRetry={() => void role.refetch()} />
    )
  }
  return role.data ? <RoleDetailView key={role.data.id} role={role.data} /> : null
}

function RoleDetailView({ role }: { role: Role }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const navigate = useNavigate()
  const canWrite = can(ROLE_WRITE_PERM)
  const save = useUpdateRolePermissions()
  const [draft, setDraft] = useState(role.permissions)
  const [selected, setSelected] = useState<string | null>(role.permissions[0] ?? null)
  const [tab, setTab] = useState('permissions')
  const [editOpen, setEditOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const commands = useRoleCommands({
    onDeleted: () => void navigate('/access/roles'),
  })

  const granted = useMemo(() => new Set(draft), [draft])
  const { added, removed } = useMemo(() => diffPermissions(role.permissions, draft), [role.permissions, draft])
  const dirty = added.length + removed.length > 0

  const onSave = async () => {
    try {
      await save.mutateAsync({ id: role.id, permissions: draft })
      toast.success(t('access.detail.saved'))
      setReviewOpen(false)
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <>
      <PageHeader
        backTo="/access/roles"
        eyebrow={
          <nav aria-label={t('access.breadcrumb')} className="flex items-center gap-1">
            <Link to="/access/roles" className="hover:text-foreground">
              {t('access.breadcrumb')}
            </Link>
            <ChevronRight className="size-3" />
            <span className="text-foreground">{role.name}</span>
          </nav>
        }
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {t('access.detail.title', { name: role.name })}
            <StatusBadge status={role.status} />
          </span>
        }
        description={role.description}
        actions={
          canWrite ? (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label={t('access.roles.actions.more')}>
                    <MoreVertical />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => void commands.toggleStatus(role)}>
                    <Power />
                    {t(role.status === 'active' ? 'access.roles.actions.deactivate' : 'access.roles.actions.activate')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-destructive data-[highlighted]:bg-destructive/10 [&_svg]:text-destructive" onSelect={() => commands.requestDelete(role)}>
                    <Trash2 />
                    {t('access.roles.actions.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button disabled={!dirty || save.isPending} onClick={() => setReviewOpen(true)}>
                <Save />
                {t('access.detail.save')}
              </Button>
            </>
          ) : null
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="general">{t('access.detail.tabs.general')}</TabsTrigger>
          <TabsTrigger value="permissions">{t('access.detail.tabs.permissions')}</TabsTrigger>
          <TabsTrigger value="users">{t('access.detail.tabs.users', { count: role.assignments.length })}</TabsTrigger>
          <TabsTrigger value="history">{t('access.detail.tabs.history')}</TabsTrigger>
          <TabsTrigger value="version">{t('access.detail.tabs.version')}</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <RoleGeneralCard role={role} canWrite={canWrite} onEdit={() => setEditOpen(true)} />
        </TabsContent>

        <TabsContent value="permissions" className="space-y-4">
          <div className="grid grid-cols-[minmax(0,1fr)] gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
            <PermissionMatrix value={draft} onChange={setDraft} selected={selected} onSelect={setSelected} readOnly={!canWrite} />
            <PermissionDetailPanel
              code={selected}
              granted={granted}
              role={role}
              readOnly={!canWrite}
              onGrant={(code) => setDraft((d) => toggleMany(d, [code]))}
              onViewUsers={() => setTab('users')}
            />
          </div>
          {dirty && canWrite ? (
            <ChangeSummaryBar
              added={added}
              removed={removed}
              saving={save.isPending}
              onSave={() => setReviewOpen(true)}
              onReview={() => setReviewOpen(true)}
              onDiscard={() => setDraft(role.permissions)}
            />
          ) : null}
        </TabsContent>

        <TabsContent value="users">
          <section className="rounded-2xl border bg-card p-4">
            <RoleUsersTable role={role} canWrite={canWrite} onAssign={() => setAssignOpen(true)} />
          </section>
        </TabsContent>

        <TabsContent value="history">
          <section className="rounded-2xl border bg-card p-4">
            <RoleHistory roleId={role.id} />
          </section>
        </TabsContent>

        <TabsContent value="version">
          <RoleVersionCard role={role} />
        </TabsContent>
      </Tabs>

      <RoleFormModal open={editOpen} onOpenChange={setEditOpen} role={role} onSaved={(r) => setDraft(r.permissions)} />
      <AssignUsersDialog role={assignOpen ? role : null} onOpenChange={setAssignOpen} />
      <SaveChangesModal
        open={reviewOpen && dirty}
        onOpenChange={setReviewOpen}
        added={added}
        removed={removed}
        role={role}
        saving={save.isPending}
        onConfirm={() => void onSave()}
      />
      {commands.dialog}
    </>
  )
}
